import React from "react";
import { useI18n } from "@/contexts/I18nContext";

/**
 * B35-2 — the prior year's own data source, shown next to the current year's SourceBadge:
 * "Prior year: Main GL" / "Prior year: Trial Balance upload" / "Prior year: No data".
 * Reads the `prior_source` field ("gl" | "tb" | "none") the TB screen, lead sheet and statement endpoints return.
 */
export default function PriorSourceLabel({ source, className = "" }) {
  const { t } = useI18n();
  if (!source) return null;
  const label = source === "gl" ? t("b35.priorGl") : source === "tb" ? t("b35.priorTb") : t("b35.priorNone");
  const tone = source === "gl" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : source === "tb" ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${tone} ${className}`}
          data-testid="prior-source-label" data-source={source}>
      {t("b35.priorLabel")}: {label}
    </span>
  );
}

/**
 * B35-3 — "A GL is loaded for this year, so this TB is stored but not used. Figures come from the GL."
 * Shown while the state lasts: `state` is the TB screen's `tb_stored_not_used` {current, prior}; `notice` is the
 * `/tb/upload` reply's (or the finished job's) `notice` {code, text, text_ar}. No confirm step.
 */
export function TbStoredNotUsedNotice({ state, notice, className = "" }) {
  const { t, lang } = useI18n();
  const periods = ["current", "prior"].filter((p) => state && state[p]);
  if (!periods.length && !notice) return null;
  const text = (notice && (lang === "ar" ? notice.text_ar : notice.text)) || t("b35.noticeText");
  const years = periods.map((p) => (p === "prior" ? t("b35.noticePrior") : t("b35.noticeCurrent"))).join(" · ");
  return (
    <div role="status" data-testid="tb-stored-not-used"
         className={`rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200 ${className}`}>
      <div className="font-medium">{t("b35.noticeTitle")}{years ? ` (${years})` : ""}</div>
      <div data-testid="tb-stored-not-used-text">{text}</div>
    </div>
  );
}

/** The TB page's year picker: which year the uploaded trial balance is for (sent as `period`). */
export function TbYearPicker({ value, onChange, disabled }) {
  const { t } = useI18n();
  return (
    <label className="flex items-center gap-2 text-xs">
      <span>{t("b35.tbYear")}</span>
      <select className="rounded-md border px-2 py-1 text-xs bg-background" value={value} disabled={disabled}
              onChange={(e) => onChange(e.target.value)} data-testid="tb-year-picker">
        <option value="current">{t("b35.tbYearCurrent")}</option>
        <option value="prior">{t("b35.tbYearPrior")}</option>
      </select>
    </label>
  );
}
